// Lessons, and the pure engine that walks one. A lesson is data: the content
// files under console/content/<env> say which request to start from, what each
// step expects, and what the coach says. The engine knows no hub, intent or
// question. See Design > Console, "Lessons".

import type { AskRequest, ResponseTag } from "./types.ts";

export type EditValue = number | string;
/** What an edit sets on the first question. A bid of "ask" takes the ask that the last response named. */
export type EditSet = { text?: string; maxCostPerAnswer?: number | "ask" };

export type Step =
  | { kind: "pick"; label: string; coach: string; text: string }
  | { kind: "edit"; label: string; coach: string; chip: { label: string; set: EditSet }; gateSend?: boolean }
  | { kind: "send"; label: string; coach: string; expect: ResponseTag[] }
  | { kind: "reject"; label: string; coach: string }
  | { kind: "credits"; label: string; coach: string };

export type Lesson = {
  id: string;
  number: number;
  title: string;
  eyebrow: string;
  blurb: string;
  /** The response type that the lesson shows off, for its card. */
  badge: ResponseTag;
  illustration: "answer" | "bid" | "missing" | "scope" | "refund";
  /** The request to start from; null starts empty, at the request picker. */
  start: AskRequest | null;
  steps: Step[];
  next: string | null;
};

export type LessonState = {
  lessonId: string;
  step: number;
  done: boolean;
  /** Set when a send came back as something other than the step expects. `retry` means the lesson went back to the edit before it, because the ask moved. */
  mismatch: { expected: ResponseTag[]; got: ResponseTag[]; retry?: boolean } | null;
};

export type LessonEvent =
  | { type: "picked"; what: string }
  | { type: "edited"; request: AskRequest; ask: number | null }
  | { type: "sent"; tags: ResponseTag[] }
  | { type: "rejected" }
  | { type: "credits-opened" };

export const startLesson = (lesson: Lesson): LessonState => ({ lessonId: lesson.id, step: 0, done: false, mismatch: null });
export const currentStep = (lesson: Lesson, s: LessonState): Step | null => (s.done ? null : lesson.steps[s.step] ?? null);

const same = (a: number | undefined, b: number) => a !== undefined && Math.abs(a - b) < 1e-9;

/** Resolves an edit's values against what the last response said. */
export type ResolvedSet = { text?: string; maxCostPerAnswer?: number };

export function resolveSet(set: EditSet, ask: number | null): ResolvedSet | null {
  const out: ResolvedSet = {};
  if (set.text !== undefined) out.text = set.text;
  if (set.maxCostPerAnswer === "ask") {
    if (ask === null) return null;
    out.maxCostPerAnswer = ask;
  } else if (set.maxCostPerAnswer !== undefined) out.maxCostPerAnswer = set.maxCostPerAnswer;
  return out;
}

export function satisfiesEdit(request: AskRequest, set: EditSet, ask: number | null): boolean {
  const want = resolveSet(set, ask);
  if (!want) return false;
  const q = request.questions?.[0];
  if (!q) return false;
  if (want.text !== undefined && (q.text ?? "").trim() !== want.text.trim()) return false;
  if (want.maxCostPerAnswer !== undefined && !same(q.maxCostPerAnswer, want.maxCostPerAnswer)) return false;
  return true;
}

const advance = (lesson: Lesson, s: LessonState): LessonState => {
  const step = s.step + 1;
  return { ...s, step, done: step >= lesson.steps.length, mismatch: null };
};

/** The next state after something happened. Events that the current step isn't waiting for change nothing. */
export function nextState(lesson: Lesson, s: LessonState, e: LessonEvent): LessonState {
  const step = currentStep(lesson, s);
  if (!step) return s;
  switch (step.kind) {
    case "pick": return e.type === "picked" ? advance(lesson, s) : s;
    case "edit": return e.type === "edited" && satisfiesEdit(e.request, step.chip.set, e.ask) ? advance(lesson, s) : s;
    case "send":
      if (e.type !== "sent") return s;
      if (e.tags.some((t) => step.expect.includes(t))) return advance(lesson, s);
      // Intent routing is a model call and can pick an intent other than the one a PriceTooLow named, so a bid raised to "the ask" can still fall short. Go back to that edit.
      const before = lesson.steps[s.step - 1];
      if (e.tags.includes("PriceTooLow") && before?.kind === "edit" && before.chip.set.maxCostPerAnswer === "ask")
        return { ...s, step: s.step - 1, mismatch: { expected: step.expect, got: e.tags, retry: true } };
      return { ...s, mismatch: { expected: step.expect, got: e.tags } };
    case "reject": return e.type === "rejected" ? advance(lesson, s) : s;
    case "credits": return e.type === "credits-opened" ? advance(lesson, s) : s;
  }
}

/** Whether the lesson is waiting on a step of this kind, so the Console points at its control. */
export const waitingOn = (lesson: Lesson, s: LessonState, kind: Step["kind"]): boolean => currentStep(lesson, s)?.kind === kind;

/** Whether the lesson holds Send back: while it waits for an edit that gates it. */
export function sendGated(lesson: Lesson, s: LessonState): boolean {
  const step = currentStep(lesson, s);
  return step?.kind === "edit" && step.gateSend === true;
}

/** The ghost chip to offer now, with its values resolved, or null when there is none yet. */
export function chipFor(lesson: Lesson, s: LessonState, ask: number | null): { label: string; set: { text?: string; maxCostPerAnswer?: number } } | null {
  const step = currentStep(lesson, s);
  if (step?.kind !== "edit") return null;
  const set = resolveSet(step.chip.set, ask);
  return set ? { label: step.chip.label, set } : null;
}

export const checklist = (lesson: Lesson, s: LessonState): { label: string; done: boolean }[] =>
  lesson.steps.map((step, i) => ({ label: step.label, done: s.done || i < s.step }));

/** The coach's words: what the mismatch means, else the step's own text, else the finish. */
export function coachText(lesson: Lesson, s: LessonState): string {
  if (s.mismatch?.retry) return "The hub's ask came back higher this time, so your bid is still under it. Raise your bid to the new ask.";
  if (s.mismatch) return `That came back as ${s.mismatch.got.join(", ") || "nothing"}, not ${s.mismatch.expected.join(" or ")}. Hubs route questions with a model, so a lesson can go a different way. Revert to try again, or skip the lesson.`;
  const step = currentStep(lesson, s);
  return step ? step.coach : "That's the lesson.";
}
